import { Component, OnInit, OnDestroy, ViewChild, Input, AfterViewInit, forwardRef, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { Logger } from 'src/app/api/helper/app-error-logger';

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MyApp } from 'src/app/api/helper/app';
import { AlertController, IonInput } from '@ionic/angular';
import { BarcodeAuthGuard } from '../../barcode.guard';
import { UiHelper } from 'src/app/api/helper/ui-helper';
import { Utils } from 'src/app/api/helper/utils';
import { SystemService } from 'src/app/api/system.service';
import { NfcEvent } from 'src/app/api/model/model';
import { I18N } from 'src/app/api/helper/i18n';

const log = new Logger('InputText');

@Component({
    selector: 'app-input-text',
    templateUrl: './input-text.component.html',
    styleUrls: ['./input-text.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => InputText),
        multi: true
    }],
    standalone: false
})
export class InputText implements ControlValueAccessor, OnInit, OnDestroy, AfterViewInit, OnChanges {
    @ViewChild('input', { read: IonInput, static: true }) input: IonInput;

    @Input() titel: string;
    @Input() lines = 'none';

    @Input() disabled = false;
    @Input() readOnly = false;
    @Input() enterkeyhint = 'done';
    @Input() pattern = '.*';
    @Input() required = false;
    @Input() clearInput = false;
    @Input() isInvalid = false;
    @Input() uppercase = false;
    @Input() scanNfcButton = false;
    @Input() scanBarcodeButton = false;
    @Input() inputmode = 'text';
    @Input() labelPosition: 'stacked' = undefined;

    /**
     * Ein Validierungsstring. Wird z.B. aus den Positions-Eigenschaften gefüllt.
     */
    @Input() validation: string = null;

    @Output() valueChanged = new EventEmitter<any>();
    @Output() lostFocus = new EventEmitter<any>();

    subscriptions: Subscription[] = [];

    isVisible = false;
    isExtraButtonZeile = false;
    inputSlot: any = 'end';

    autocapitalize: string = null;

    isClearButtonVisible = false;
    isEmptySpaceVisible = false;

    app = MyApp.current;

    private _value: string;

    nfcAlert: any;
    nfcRegistrationId: number;

    // Whatever name for this (myValue) you choose here, use it in the .html file.
    public get myValue(): string { return this._value }

    public set myValue(v: string) {
        // log?.debug('set myValue: ' + v);

        if (this.uppercase && v && typeof (v) === 'string') {
            v = v.toUpperCase();
        }

        if (v !== this._value) {
            this._value = v;
            this.onChange(v);
        }
    }

    constructor(
        private alertController: AlertController,
        private cdr: ChangeDetectorRef,
        private systemService: SystemService,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        // log?.debug('changed ' + this.titel, changes);

        this.updateType();

        if (changes?.pattern || changes?.validation) {
            if (this.isVisible) {
                // Der Regex wurde aktualisiert. Die Validierung muss erneut angestoßen werden
                this.validate();
            }
        }
    }

    onChange = (_) => {
        if (this.isVisible) {
            this.validate();
        }
    };

    onTouched = () => { };

    writeValue(value: any): void {
        // log?.debug('writeValue ' + this.titel, value);

        this.myValue = value;

        // setTimeout(() => {
        //     this.validate();
        // });
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
    }

    ngOnInit() {
        // log?.debug('ngOnInit ' + this.titel);

        this.isVisible = true;
        this.updateType();
        this.validate();
    }

    async ngAfterViewInit() {
        // log?.debug('ngAfterViewInit ' + this.titel);

        setTimeout(() => {
            this.validate();

            this.cdr.detectChanges();
        });
    }

    ngOnDestroy() {
        this.systemService.unregisterNfcReceiver(this.nfcRegistrationId);
    }

    async ionViewWillEnter() {
    }

    ionViewDidEnter() {
    }

    ionViewWillLeave() {
    }

    onClearValue() {
        if (!this.readOnly) {
            this.myValue = null;
            this.valueChanged.next(this._value);

            this.input.setFocus();
        }
    }

    setFocus() {
        try {
            this.input.setFocus();
        } catch (err) {
            log.warn(Utils.getErrorMessage(err), err);
        }
    }

    updateType() {
        this.isClearButtonVisible = false;
        this.isExtraButtonZeile = false;

        if (this.uppercase) {
            this.autocapitalize = 'characters';
        } else {
            this.autocapitalize = null;
        }

        // if (this.plusMinusButtons && !this.disabled && !this.readOnly) {
        //     this.isPlusButtonVisible = true;
        //     this.isMinusButtonVisible = true;
        // }

        if (this.clearInput && !this.scanNfcButton && !this.scanBarcodeButton) {
            this.isClearButtonVisible = true;
        }

        if (!this.scanBarcodeButton && !this.scanNfcButton && !this.isClearButtonVisible) {
            this.isEmptySpaceVisible = true;
        }

        if (this.scanBarcodeButton && this.scanNfcButton) {
            this.isExtraButtonZeile = true;
        }

        if (this.labelPosition === 'stacked') {
            this.inputSlot = ''; // undefined;
        } else {
            this.inputSlot = 'end';
        }
    }

    onBlur(e: any) {
        // log?.debug('onBlur ' + this.titel);
        this.lostFocus.next(e);
        this.valueChanged.next(this._value);
        this.validate();
    }

    onValueChange(e: any) {
        // log?.debug('onValueChange ' + this.titel);
        this.valueChanged.next(this._value);
        this.validate();
    }

    validate(): boolean {
        // log?.debug('validate ' + this.titel + ': ' + this.pattern + ', value=' + this._value);
        if (this.pattern && this.pattern != '.*') {
            this.isInvalid = false;

            try {
                const wert = Utils.trimToEmpty(this._value);

                if (!wert.match(this.pattern)) {
                    this.isInvalid = true;
                }

            } catch (err) {
                log.warn('Fehler in Regex ' + this.pattern + ': ' + Utils.getErrorMessage(err));
            }
        } else {
            this.isInvalid = false;
        }

        return !this.isInvalid;
    }

    async scanBarcode() {
        log?.debug('scanEigenschaftBarcode: ' + this.titel);

        BarcodeAuthGuard.barcodeScanAktiv = true;

        try {
            let barcodeData = await MyApp.scanBarcode();

            log?.debug(`scanEigenschaftBarcode ${this.titel}: ${barcodeData.text}`);

            if (barcodeData?.text) {
                let barcodeText = barcodeData.text;

                // Soll ein Rinderpass oder eine Ohrmarke gescannt werden?
                if (this.isOhrmarkeFeld()) {
                    barcodeText = this.getOhrmarkeBarcodeText(barcodeData);
                }

                // Lebensnummer
                if (this.isLebensnummerFeld()) {
                    barcodeText = this.getLebensnummerBarcodeText(barcodeData);
                }

                // Soll ein Geburtsdatum gescannt werden?
                if (this.isDatumFeld()) {
                    barcodeText = this.getDatumBarcodeText(barcodeData);
                }

                this.myValue = barcodeText;
            }
        } catch (err) {
            UiHelper.showErrorLight(err);
        }

        // Muss aufgerufen werden, sonst keine Validierung und es bleibt rot hinterlegt
        this.validate();

        setTimeout(() => BarcodeAuthGuard.barcodeScanAktiv = false, 500);
    }

    getOhrmarkeBarcodeText(barcodeData: any): string {
        let text = barcodeData.text as string;

        if (!text) {
            return text;
        }

        if (text.length === 12) {
            // Strichcode auf der Ohrmarke
            return 'DE' + text.substring(0, 11);
        } else if (text.length === 16) {
            // Strichcode auf dem Rinderpass. 276000955362661(2)            
            // Für OFK soll hier aus dem 27600 ein DE gemacht werden, macht vlt. für alle Sinn?
            text = text.substring(0, 15);

            const mapping = {
                '27600': 'DE',
            }

            for (const [key, value] of Object.entries(mapping)) {
                if (text.startsWith(key)) {
                    text = text.replace(key, value);
                    break;
                }
            }

            return text;
        } else {
            return text;
        }
    }
    
    getLebensnummerBarcodeText(barcodeData: any): string {
        let text = barcodeData.text as string;

        if (!text) {
            return text;
        }

        if (text.length === 15) {
            // Strichcode auf der Lebensnummer. 276 422 220013107         
            // Für OFK soll hier aus dem 276 ein DE gemacht werden, macht vlt. für alle Sinn?
            const mapping = {
                '276': 'DE',
                '040': 'AT',
                '756': 'CH',
            }

            for (const [key, value] of Object.entries(mapping)) {
                if (text.startsWith(key)) {
                    text = text.replace(key, value);
                    break;
                }
            }

            return text; 

        }else if (text.length === 16) {
            // Strichcode auf der Lebensnummer. 276 422 220013107 + Prüfnummer      
            // Für OFK soll hier aus dem 276 ein DE gemacht werden, macht vlt. für alle Sinn?
            text = text.substring(0, 15);

            const mapping = {
                '276': 'DE',
                '040': 'AT',
                '756': 'CH',
            }

            for (const [key, value] of Object.entries(mapping)) {
                if (text.startsWith(key)) {
                    text = text.replace(key, value);
                    break;
                }
            }

            return text; 
        } 
        else {
            return text;
        }
    }

    isOhrmarkeFeld() {
        if (this.titel) {
            if (this.titel.includes('Rinderpass') || this.titel.includes('Ohrmarke')) {
                return true;
            }
        }

        return false;
    }

    isLebensnummerFeld() {
        if (this.titel) {
            if (this.titel.includes('Lebensnummer')) {
                return true;
            }
        }

        return false;
    }

    getDatumBarcodeText(barcodeData: any): string {
        let text = barcodeData.text as string;

        // Wenn Geburtsdatum gescannt wird, muss es immer in dem Format kommen
        if (!/^\d{10}$/.test(text)) {
            throw new Error('Ungültiges Format. Erwartet: TTMMYYYYxx');
        }

        const tag = text.substring(0, 2);
        const monat = text.substring(2, 4);
        const jahr = text.substring(4, 8);

        return `${tag}.${monat}.${jahr}`;
    }

    isDatumFeld() {
        if (this.titel) {
            if (this.titel.includes('Geburtsdatum') || this.titel.includes('Datum')) {
                return true;
            }
        }

        return false;
    }

    async scanNfc() {
        log?.debug('scanNfc');

        if (this.readOnly || this.disabled) {
            return;
        }

        this.nfcRegistrationId = this.systemService.registerNfcReceiver(100, (nfcEvent) => this.verarbeiteNfcEvent(nfcEvent));

        BarcodeAuthGuard.barcodeScanAktiv = true;

        if (this.nfcAlert) {
            this.nfcAlert.dismiss();
            this.nfcAlert = null;
        }

        this.nfcAlert = await this.alertController.create({
            mode: 'ios',
            message: I18N.instant('NFCErkennungLaeuft'),
            buttons: [{
                text: I18N.instant('Abbrechen'),
                role: 'cancel',
                cssClass: 'secondary',
                handler: () => {
                }
            },
            ]
        });

        if (!MyApp.isCordovaAvailable()) {
            setTimeout(() => {
                this.nfcAlert.dismiss();
                this.myValue = 'DEMO12345';
            }, 2000);
        }

        await this.nfcAlert.present();

        await this.nfcAlert.onDidDismiss();

        this.systemService.unregisterNfcReceiver(this.nfcRegistrationId);

        // if (this.auftragdetails) {
        //     this.auftragdetails.starteAuftragWennErforderlich('scanEigenschaftNfc');
        // }

        setTimeout(() => BarcodeAuthGuard.barcodeScanAktiv = false, 500);
    }

    verarbeiteNfcEvent(event: NfcEvent) {
        let barcode: string;

        if (MyApp.current.NfcPayloadVerwenden && event.payload) {
            barcode = event.payload;
        } else {
            barcode = event.seriennummer;
        }

        this.myValue = barcode;

        if (this.nfcAlert) {
            this.nfcAlert.dismiss();
            this.nfcAlert = null;
        }
    }

    async onEnter(e) {
        if (this.enterkeyhint === 'next') {
            Utils.focusNextInputElement();

            // const domElement = e.srcElement?.parentElement as HTMLIonInputElement;

            // if (domElement) {
            //     const nextField = domElement.nextElementSibling as HTMLIonInputElement;
            //     if (nextField) {
            //         nextField.setFocus();
            //     }
            // }
        } else {
            this.app.closeKeyboard(e);
        }
    }
}
